import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { Loader2, ShieldAlert } from "lucide-react";
import { useNewOrderAlerts, NotificationToggle } from "@/components/admin/NewOrderAlerts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/lib/api";
import OrdersTab from "@/components/admin/OrdersTab";
import SettingsTab from "@/components/admin/SettingsTab";
import UsersTab from "@/components/admin/UsersTab";

function Dashboard() {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  useEffect(() => { api("get", "/me").then((r) => setMe(r.data)).catch(() => setMe({ role: "error" })); }, [api]);
  const staff = ["admin", "verifikator", "operator"].includes(me?.role);
  useNewOrderAlerts(api, bump, staff);

  if (!me) return <div className="p-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF5A00]" /></div>;
  if (!["admin", "verifikator", "operator"].includes(me.role)) {
    return (
      <div data-testid="pending-access" className="max-w-xl mx-auto px-4 py-20 text-center">
        <ShieldAlert className="w-12 h-12 text-[#FF5A00] mx-auto" />
        <h1 className="font-display font-black uppercase text-4xl mt-4">Menunggu Akses</h1>
        <p className="text-neutral-400 mt-2">Akun {me.email} belum diberi akses. Minta admin LENSA JUARA untuk menjadikan Anda Operator atau Admin.</p>
      </div>
    );
  }
  const isAdmin = me.role === "admin";
  const canVerify = isAdmin || me.role === "verifikator";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-6">
        <h1 className="font-display font-black uppercase italic text-4xl">Dashboard</h1>
        <div className="flex flex-wrap items-center gap-2">
          <NotificationToggle />
          <span data-testid="current-role" className="text-sm border border-white/15 px-3 py-1">{me.email} · <b className="text-[#FF5A00] uppercase">{me.role}</b>{me.role === "operator" && " (lihat saja)"}{me.role === "verifikator" && " (verifikasi & edit pesanan)"}</span>
        </div>
      </div>
      <Tabs defaultValue="orders">
        <TabsList className="bg-[#121212] border border-white/10 rounded-none">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none">Pesanan</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-none">Pengaturan</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users" className="rounded-none">Pengguna</TabsTrigger>}
        </TabsList>
        <TabsContent value="orders"><OrdersTab canVerify={canVerify} isAdmin={isAdmin} refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="settings"><SettingsTab isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="users"><UsersTab me={me} /></TabsContent>}
      </Tabs>
    </div>
  );
}

export default function AdminPage() {
  return (
    <>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
